// A stock take on the station (plan §9.1, §9.5; D-049), as pure functions so every rule is
// unit-tested: which rows a count covers, what a line says, the variances and their value,
// which products need a manager's approval (per product, on its net change across lots,
// D-049), and the `count.submit` payload.
//
// "Not counted" and "counted 0" are different things on screen (a line nobody looked at vs
// stock that isn't there). The server counts every row of the scope with stock that has no
// line as 0 (D-049), so a count is only submitted once every line is counted or someone has
// said "count the rest as 0"; with a product scope, products nobody counted can be left out
// of the scope instead.

import {
  type ApprovalCheck,
  type ApprovalThresholds,
  type Cents,
  cents,
  compareQty,
  isZero,
  mulQty,
  qty,
  qtyToString,
  requiresApproval,
  subQty,
  sumQty,
} from '@enp/domain';
import type { StockRow } from '../stock/stock-data.js';

export type CountScope =
  | { kind: 'location'; locationId: string }
  | { kind: 'products'; locationId: string; productIds: string[] };

/** A lot found without a label and given one during this count (`lot.create_opening`). */
export interface NewLotInfo {
  lotCode: string;
  bestBefore: string | null;
  producedAt?: string;
  /** The command that created it (sent before the count). */
  commandId: string;
}

export interface CountLine {
  /** `product|lot|state`. */
  key: string;
  productId: string;
  productName: string;
  unit: string | null;
  lotId: string | null;
  lotCode: string | null;
  bestBefore: string | null;
  state: string;
  /** What the station's data held when the line was made; null for a line added here. */
  expected: string | null;
  /** The count, or null while not counted. */
  counted: string | null;
  /** Standard cost per base unit, if known. */
  unitCostCents: number | null;
  newLot?: NewLotInfo;
  /** Added during the count (a scan, a pick, unlabelled stock), not expected here. */
  added?: boolean;
  /** When it was last counted on this station (a move after this makes it stale, CR4-6). */
  countedAt?: string;
}

/**
 * Stock this station moved or wrote off at the count's location after the count started
 * (CR4-6): a line counted before it no longer matches the shelf, and submitting would undo
 * the move. The review asks for those lines to be counted again.
 */
export interface CountMovement {
  /** The command that did it. */
  commandId: string;
  kind: 'move' | 'write_off';
  at: string;
  productId: string;
  lotId: string | null;
  /** The state the stock left (a move) or was written off from. */
  fromState: string;
  /** A move's target state. */
  toState?: string;
  qty: string;
}

export interface CountDraft {
  /** The count's id (made on the station). */
  countId: string;
  scope: CountScope;
  locationName: string;
  blind: boolean;
  startedAt: string;
  staffId: string;
  lines: CountLine[];
  /** Moves and write-offs at this location since the count started (CR4-6). */
  movements?: CountMovement[];
}

export const lineKey = (productId: string, lotId: string | null, state: string) =>
  `${productId}|${lotId ?? ''}|${state}`;

/** The lines a count starts with: the scope's rows with stock here, as the station knows them. */
export function expectedLines(scope: CountScope, rows: readonly StockRow[]): CountLine[] {
  const inScope = rows.filter(
    (r) =>
      r.location_id === scope.locationId &&
      (scope.kind === 'location' || scope.productIds.includes(r.product_id)) &&
      !isZero(r.qty),
  );
  return inScope.map((r) => ({
    key: lineKey(r.product_id, r.lot_id, r.state),
    productId: r.product_id,
    productName: r.product_name ?? 'Unknown product',
    unit: r.base_uom,
    lotId: r.lot_id,
    lotCode: r.lot_code,
    bestBefore: r.best_before,
    state: r.state,
    expected: qtyToString(qty(r.qty)),
    counted: null,
    unitCostCents: r.standard_cost_cents,
  }));
}

/**
 * Brings the expected figures up to what the station holds now (a sync during the count),
 * and adds rows of the scope that turned up since the count started: the server counts a
 * row of the scope with no line as 0, so every one of them must be on screen.
 */
export function syncExpected(draft: CountDraft, rows: readonly StockRow[]): CountDraft {
  const now = new Map(expectedLines(draft.scope, rows).map((l) => [l.key, l]));
  let changed = false;
  const lines = draft.lines.map((l) => {
    const current = now.get(l.key);
    now.delete(l.key);
    const expected = current?.expected ?? (l.added ? l.expected : '0');
    if (expected === l.expected) return l;
    changed = true;
    return { ...l, expected };
  });
  for (const extra of now.values()) {
    changed = true;
    lines.push(extra);
  }
  return changed ? { ...draft, lines } : draft;
}

/** Adds a line (or returns the draft as is when the line is there already). */
export function addLine(draft: CountDraft, line: Omit<CountLine, 'key'>): CountDraft {
  const key = lineKey(line.productId, line.lotId, line.state);
  if (draft.lines.some((l) => l.key === key)) return draft;
  const scope =
    draft.scope.kind === 'products' && !draft.scope.productIds.includes(line.productId)
      ? { ...draft.scope, productIds: [...draft.scope.productIds, line.productId] }
      : draft.scope;
  return { ...draft, scope, lines: [...draft.lines, { ...line, key, added: true }] };
}

/** A figure from the pad as a quantity: "4." (half typed) counts as "4". */
export function countedFrom(typed: string): string {
  const text = typed.endsWith('.') ? typed.slice(0, -1) : typed;
  return text === '' ? '0' : text;
}

/** A line back to "not counted". */
function uncounted(line: CountLine): CountLine {
  const { countedAt: _, ...rest } = line;
  return { ...rest, counted: null };
}

/** Sets a line's count (a decimal string), or back to "not counted" with null. */
export function setCounted(
  draft: CountDraft,
  key: string,
  counted: string | null,
  now: Date = new Date(),
): CountDraft {
  return {
    ...draft,
    lines: draft.lines.map((l) => {
      if (l.key !== key) return l;
      return counted === null ? uncounted(l) : { ...l, counted, countedAt: now.toISOString() };
    }),
  };
}

/** Counts every line nobody counted as 0 ("not found"). */
export function countRestAsZero(draft: CountDraft, now: Date = new Date()): CountDraft {
  return {
    ...draft,
    lines: draft.lines.map((l) =>
      l.counted === null ? { ...l, counted: '0', countedAt: now.toISOString() } : l,
    ),
  };
}

/**
 * Leaves products with no counted line out of a product-scope count (their stock is not
 * touched). A location count can't: everything at the location is in it.
 */
export function leaveOutUncounted(draft: CountDraft): CountDraft {
  if (draft.scope.kind !== 'products') return draft;
  const counted = new Set(draft.lines.filter((l) => l.counted !== null).map((l) => l.productId));
  return {
    ...draft,
    scope: { ...draft.scope, productIds: draft.scope.productIds.filter((p) => counted.has(p)) },
    lines: draft.lines.filter((l) => counted.has(l.productId)),
  };
}

/** Lines nobody counted yet. */
export function notCounted(draft: CountDraft): CountLine[] {
  return draft.lines.filter((l) => l.counted === null);
}

/** Products (in a product scope) with no counted line at all: they can be left out. */
export function productsNotCounted(draft: CountDraft): string[] {
  if (draft.scope.kind !== 'products') return [];
  const counted = new Set(draft.lines.filter((l) => l.counted !== null).map((l) => l.productId));
  return draft.scope.productIds.filter((p) => !counted.has(p));
}

/** One line's difference: counted − expected (null until counted). */
export function lineVariance(line: CountLine): string | null {
  if (line.counted === null) return null;
  return qtyToString(subQty(line.counted, line.expected ?? '0'));
}

/** A product's part of the review: its net change across its lots, and the approval check. */
export interface ProductReview {
  productId: string;
  productName: string;
  unit: string | null;
  /** System level before (sum of the expected rows). */
  levelBefore: string;
  /** Counted in total (not-counted lines as 0). */
  counted: string;
  /** Net change (counted − level before). */
  change: string;
  /** |change| × cost; null without a cost. */
  valueCents: Cents | null;
  /** Signed value of the change (for totals); null without a cost. */
  signedValueCents: Cents | null;
  approval: ApprovalCheck;
}

export interface CountReview {
  products: ProductReview[];
  /** Products whose change needs a manager (D-100). */
  needsApproval: ProductReview[];
  /** Lines that differ from what the station expected. */
  changedLines: number;
  /** Sum of the signed values we know (null when no changed product has a cost). */
  netValueCents: Cents | null;
  /** Some changed product has no standard cost (its value is unknown). */
  someWithoutCost: boolean;
  /** Changed products without a standard cost, by name (not in the value). */
  withoutCost: string[];
}

/**
 * The review of a count against the station's figures (D-049: per product, net). Only lines
 * someone decided on (counted, or said to be 0) are in it, so every figure on the review
 * tells the same story while lines are still open (UI4S-2); a count is submitted only once
 * every line is decided, and then this is the whole count.
 */
export function reviewCount(draft: CountDraft, thresholds: ApprovalThresholds): CountReview {
  const byProduct = new Map<string, CountLine[]>();
  for (const l of draft.lines) {
    if (l.counted === null) continue;
    byProduct.set(l.productId, [...(byProduct.get(l.productId) ?? []), l]);
  }
  const products: ProductReview[] = [];
  let changedLines = 0;
  for (const [productId, lines] of byProduct) {
    const first = lines[0] as CountLine;
    const levelBefore = sumQty(lines.map((l) => l.expected ?? '0'));
    const counted = sumQty(lines.map((l) => l.counted ?? '0'));
    const change = subQty(counted, levelBefore);
    changedLines += lines.filter((l) => {
      const v = lineVariance(l);
      return v !== null && !isZero(v);
    }).length;
    const unitCost = first.unitCostCents === null ? null : cents(first.unitCostCents);
    const approval = requiresApproval(
      'count_adjustment',
      { qty: change, unitCostCents: unitCost, levelBefore },
      thresholds,
    );
    const signed = unitCost === null ? null : mulQty(unitCost, change);
    products.push({
      productId,
      productName: first.productName,
      unit: first.unit,
      levelBefore: qtyToString(levelBefore),
      counted: qtyToString(counted),
      change: qtyToString(change),
      valueCents: approval.valueCents,
      signedValueCents: signed,
      approval,
    });
  }
  const changed = products.filter((p) => !isZero(p.change));
  const costed = changed.filter((p) => p.signedValueCents !== null);
  return {
    products,
    needsApproval: products.filter((p) => p.approval.required),
    changedLines,
    netValueCents:
      costed.length === 0
        ? null
        : cents(costed.reduce((s, p) => s + (p.signedValueCents as number), 0)),
    someWithoutCost: changed.some((p) => p.signedValueCents === null),
    withoutCost: changed.filter((p) => p.signedValueCents === null).map((p) => p.productName),
  };
}

/** The `count.submit` payload (without `approval`, which the data layer adds). */
export function countPayload(
  draft: CountDraft,
  number: string,
  countedAt: string,
  note?: string,
): Record<string, unknown> {
  const lines = draft.lines
    .filter((l) => l.counted !== null)
    .map((l) => ({
      product_id: l.productId,
      lot_id: l.lotId,
      state: l.state,
      counted_qty: qtyToString(qty(l.counted as string)),
    }));
  const scope =
    draft.scope.kind === 'location'
      ? { kind: 'location', location_id: draft.scope.locationId }
      : {
          kind: 'products',
          location_id: draft.scope.locationId,
          product_ids: draft.scope.productIds,
        };
  return {
    count_id: draft.countId,
    number,
    scope,
    blind: draft.blind,
    // When counting began (CR4-6): the server flags moves between this and `counted_at`.
    started_at: draft.startedAt,
    counted_at: countedAt,
    lines,
    ...(note && note.trim() ? { note: note.trim() } : {}),
  };
}

// States in the order stock goes through them (fresh first), for the lines of one lot.
const STATE_ORDER = ['fresh', 'default', 'markdown', 'day_old', 'clearance', 'expired', 'removed'];
const stateRank = (state: string) => {
  const i = STATE_ORDER.indexOf(state);
  return i < 0 ? STATE_ORDER.length : i;
};

/** Lines ordered for counting: product, then best-before, lot, state (fresh first). */
export function sortLines(lines: readonly CountLine[]): CountLine[] {
  return [...lines].sort(
    (a, b) =>
      a.productName.localeCompare(b.productName) ||
      (a.bestBefore ?? '9999').localeCompare(b.bestBefore ?? '9999') ||
      (a.lotCode ?? '').localeCompare(b.lotCode ?? '') ||
      stateRank(a.state) - stateRank(b.state) ||
      a.state.localeCompare(b.state),
  );
}

/** Counted lines, of all lines. */
export function progress(draft: CountDraft): { counted: number; total: number } {
  return {
    counted: draft.lines.filter((l) => l.counted !== null).length,
    total: draft.lines.length,
  };
}

/** True when the counted figure differs from the expected one. */
export function differs(line: CountLine): boolean {
  return line.counted !== null && compareQty(line.counted, line.expected ?? '0') !== 0;
}

/** Whether `line` is a row `movement` touched (its lot, in the state it left or entered). */
function touches(movement: CountMovement, line: CountLine): boolean {
  return (
    line.productId === movement.productId &&
    (line.lotId ?? null) === (movement.lotId ?? null) &&
    (line.state === movement.fromState || line.state === movement.toState)
  );
}

/** A move or write-off that makes lines counted before it wrong (CR4-6). */
export interface MovementWarning {
  movement: CountMovement;
  productName: string;
  unit: string | null;
  lotCode: string | null;
  /** Lines counted before the movement: count them again. */
  keys: string[];
  /** The states of those lines. */
  states: string[];
}

/**
 * Moves and write-offs made here after the count started that touched lines already counted
 * before them. Lines not counted yet, or counted after the move, are right as they are.
 */
export function movementWarnings(draft: CountDraft): MovementWarning[] {
  const out: MovementWarning[] = [];
  for (const m of draft.movements ?? []) {
    const stale = draft.lines.filter(
      (l) => l.counted !== null && touches(m, l) && (l.countedAt ?? draft.startedAt) < m.at,
    );
    if (stale.length === 0) continue;
    const first = stale[0] as CountLine;
    out.push({
      movement: m,
      productName: first.productName,
      unit: first.unit,
      lotCode: first.lotCode,
      keys: stale.map((l) => l.key),
      states: stale.map((l) => l.state),
    });
  }
  return out;
}

/** Sets the lines back to "not counted", to count them again. */
export function recount(draft: CountDraft, keys: readonly string[]): CountDraft {
  const set = new Set(keys);
  return { ...draft, lines: draft.lines.map((l) => (set.has(l.key) ? uncounted(l) : l)) };
}

/**
 * The movement a saved command is, when it touches the count's scope after the count
 * started (a state move or write-off at its location, of a product in it); else undefined.
 */
export function movementOf(
  draft: CountDraft,
  envelope: { command_id: string; type: string; payload: unknown; occurred_at: string },
): CountMovement | undefined {
  if (envelope.type !== 'stock.state_move' && envelope.type !== 'writeoff.request') return;
  const p = (envelope.payload ?? {}) as Record<string, unknown>;
  if (p['location_id'] !== draft.scope.locationId) return;
  const productId = typeof p['product_id'] === 'string' ? p['product_id'] : undefined;
  if (!productId) return;
  if (draft.scope.kind === 'products' && !draft.scope.productIds.includes(productId)) return;
  if (envelope.occurred_at < draft.startedAt) return;
  const base = {
    commandId: envelope.command_id,
    at: envelope.occurred_at,
    productId,
    lotId: typeof p['lot_id'] === 'string' ? p['lot_id'] : null,
    qty: typeof p['qty'] === 'string' ? p['qty'] : '0',
  };
  return envelope.type === 'stock.state_move'
    ? {
        ...base,
        kind: 'move',
        fromState: String(p['from_state'] ?? ''),
        toState: String(p['to_state'] ?? ''),
      }
    : { ...base, kind: 'write_off', fromState: String(p['state'] ?? '') };
}

/** Adds a movement to the count (once per command). */
export function addMovement(draft: CountDraft, movement: CountMovement): CountDraft {
  const list = draft.movements ?? [];
  if (list.some((m) => m.commandId === movement.commandId)) return draft;
  return { ...draft, movements: [...list, movement] };
}
